
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { styled } from "@mui/material/styles";
import { ComponentItem } from "designSystem/types/component-item-types";
import { FlexRowCenter } from "designSystem/index";
import Autocomplete from "designSystem/components/select/AutoCompleteList";
import { clickableStyle } from "designSystem/styles/style-functions";
import OverflowText from "designSystem/components/text/OverflowText";
import React, { FunctionComponent, SyntheticEvent } from "react";
import { useLocale } from "designSystem/locale";

const StyledFlexRowCenter = styled(FlexRowCenter)(({
	theme,
}) => ({
	cursor: "pointer",
	...clickableStyle(theme),
}));

interface AutoCompleteSelectProps {

	/** custom styles */
	className?: string;

	/** flag to disable or enable search */
	enableSearch: boolean;

	/** font size for overflow text */
	fontSize?: number;

	/** flag to indicate loading */
	loading?: boolean;

	/** the store of the menu */
	options: ComponentItem[];

	/** callback when clicking a dropdown menu item */
	onChange: (event: SyntheticEvent<Element, Event>, value: ComponentItem) => void;

	/** callback when clicking on the text box */
	onClickName: () => void;

	/** extension point when item is de-selected */
	onBlur: () => void;

	/** localization resource for button */
	res?: string;

	/** localization resource parameters for button */
	resParams?: Array<number | string>;

	/** text value inside text box */
	labelText: string;

	/** boolean to show the search bar or text box */
	searching: boolean;

	/** search value inside search box */
	searchValue?: ComponentItem;

	/** width of search box */
	searchWidth: number;
}

const AutoCompleteSelect: FunctionComponent<AutoCompleteSelectProps> = (props) => {
	const {
		className,
		enableSearch,
		fontSize,
		labelText,
		loading,
		onClickName,
		onChange,
		onBlur,
		options,
		res,
		resParams,
		searching,
		searchValue,
		searchWidth,
	} = props;

	const loc = useLocale();

	const textValue = res ? loc.resource(res, resParams) : labelText;

	return (
		<>
			{searching ? (
				<Autocomplete<ComponentItem>
				/**
				 * The default width does not include the border, so override
				 * to create popup with same width
				 * https://github.com/mui-org/material-ui/issues/19376
				 */
					getOptionLabel={loc.getComponentItemName}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					loading={loading}
					onBlur={onBlur}
					/** Value is any in order to bypass Mui expecting that potentially onChange has an array value */
					onChange={onChange as any}
					open
					options={options || []}
					popperProps={{ style: { width: searchWidth } }}
					renderOption={(props, option) => {
						return (
							<li {...props} key={option.id}>
								<OverflowText
									text={loc.getComponentItemName(option)}
									tooltipProps={{
										placement: "bottom-start",
										PopperProps: {
											sx: {
												paddingLeft: "20px",
											},
										},
									}}
								/>
							</li>
						);
					}}
					searchTextProps={{
						onEscape: onBlur,
						placeholder: loc.resource("TypeKeywordToFilter"),
					}}
					style={{ width: searchWidth }}
					value={searchValue as any}
				/>
			)
				: (
					<StyledFlexRowCenter
						borderRadius={1}
						className={enableSearch ? `${className || ""}` : null}
						height={26}
						inline
						ml={-1}
						onClick={enableSearch ? onClickName : null}
						overflow="hidden"
						px={1}
					>
						<OverflowText
							bold
							lineHeight={20}
							size={fontSize || 16}
							text={textValue}
						/>
						{enableSearch &&
					<ArrowDropDownIcon style={{ marginLeft: 4 }} />
						}
					</StyledFlexRowCenter>
				)}
		</>
	);
};

export default AutoCompleteSelect;